// Updated: 2026-09-06
import type {
  SearchCollectionsResponse,
  GetMyCollectionsResponse,
  GetCollectionResponse,
  CreateCollectionResponse,
  UpdateCollectionResponse,
  ArchiveCollectionResponse,
  RestoreCollectionResponse,
  AddPatternToCollectionResponse,
  RemovePatternFromCollectionResponse,
  FollowCollectionResponse,
  UnfollowCollectionResponse,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from '@mrb91002/shared-types'

import { PublicApiError } from '../fetchers/errors'
import type { ListingBucket } from '../listingBuckets'

import { secureFetch, SECURE_API_BASE, API_BASE } from './helpers'

/**
 * Collection search (lambda-main `GET /api/collections/search?q=…`).
 *
 * This function is MISFILED: it lives under `secure/` but is a PUBLIC fetcher — raw `fetch` against
 * `API_BASE`, not `secureFetch` against `SECURE_API_BASE`, and `search-collections.ts` reads no
 * authorizer. `CollectionSearchPage` calls it with two arguments, so `idToken` is `undefined` and the
 * headers object is `{}`. The optional-Bearer capability is kept as-is; the folder is not "fixed" here
 * because moving the file would move the S13 audit denominator (react-query-browse-search § D6).
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`, spread
 * conditionally so a caller that omits it makes a byte-identical request (Req 6.1, 6.2). The failure
 * message is unchanged verbatim but now carries the HTTP status, so the shared retry predicate never
 * retries a 4xx (Req 6.3, 6.4). `secureFetch` is not on this path, so its token attach / proactive
 * refresh / 401-retry behavior is untouched by construction (Req 6.7).
 */
export async function searchCollections(
  query: string,
  filters: Record<string, string> = {},
  idToken?: string,
  opts?: { signal?: AbortSignal },
): Promise<SearchCollectionsResponse> {
  const params = new URLSearchParams({ q: query, ...filters })
  const url = `${API_BASE}/collections/search?${params}`
  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to search collections', resp.status)
  return resp.json() as Promise<SearchCollectionsResponse>
}

/**
 * PERSONAL — the caller's own collections (lambda-secure `GET /api/secure/my-collections`).
 *
 * Identity-VARYING: `collections/my-collections.ts` resolves the caller with `getIdentityId(event)` and
 * answers 401 without it, so the query layer keys it `['me','collections',{minimal}]` and gates it on
 * `!!idToken`. `minimal` is in the key because `?minimal=true` selects a DIFFERENT response shape from
 * the same route (react-query-profile-collections § D3).
 *
 * `opts.signal` is forwarded INTO `secureFetch`, whose `RequestInit` already accepts `signal`; nothing
 * about its token attach / proactive refresh / 401-retry is reimplemented (Req 8.3). Spread
 * conditionally, so a caller that omits `opts` passes the same `{ method: 'GET' }` it always did.
 *
 * ## `?bucket=` — ONE listing per request (content-visibility-model Requirement 5.3, task 29)
 *
 * The `includeArchived: false` this used to describe no longer exists. Task 18 gave the handler a
 * `?bucket=` parameter that narrows it to ONE `begins_with(GSI3SK, …)` key condition, which is what the
 * owner's four segments ask for. `opts.bucket` sends it.
 *
 * **Omitting it is a different request, not a neutral one.** With no parameter the handler composes the
 * three UN-SHELVED buckets — one key-condition query each, concatenated — which is a listing spanning
 * buckets and therefore not what Requirement 5.3 wants. That mode survives for `?minimal=true`, whose
 * caller (`AddToCollectionModal`) needs "every collection I could add this pattern to" rather than one
 * bucket of them, and a bucket segmented control inside an add-to-collection modal would be asking the
 * user the wrong question. `MyCollectionsPage` now always sends a bucket, so the composed default is no
 * longer reachable from the full list.
 *
 * An unrecognised value is refused by the handler with 400 rather than answered as `public`, which is why
 * `bucket` is typed `ListingBucket` here rather than `string`.
 */
export async function getUserCollections(
  minimal: boolean,
  idToken: string,
  opts?: { signal?: AbortSignal; bucket?: ListingBucket | null },
): Promise<GetMyCollectionsResponse> {
  const params = new URLSearchParams()
  if (minimal) params.set('minimal', 'true')
  if (opts?.bucket) params.set('bucket', opts.bucket)
  const qs = params.toString()
  const url = `${SECURE_API_BASE}/my-collections${qs ? `?${qs}` : ''}`
  return secureFetch(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

export async function getUserCollectionsPreview(
  isAuthenticated: boolean,
  idToken?: string,
): Promise<GetMyCollectionsResponse> {
  if (isAuthenticated && idToken) {
    return getUserCollections(true, idToken)
  }
  throw new Error('Not authenticated')
}

/**
 * The AUTHENTICATED collection read — `GET /api/secure/collections/{collectionId}`
 * (content-visibility-model, Req 4.8; task 41).
 *
 * RENAMED from `getCollection` and given the two parameters the fallback needs. The old name described
 * only half of what this route is: it is not "the" collection read, it is the SECOND attempt behind the
 * public one, and the `Authenticated` suffix matches its pattern and project siblings
 * (`getPatternAuthenticated`, `getCraftProjectAuthenticated`) so the three fallback targets are findable
 * as a set. It had no caller at the time of the rename — `queries/collection/mutations.ts` recorded that
 * absence — so nothing else moved with it.
 *
 * Handler: `lambda-secure/src/routes/collections/management/get-collection.ts`. It is OWNER-ONLY and
 * answers `404 'Collection not found'` for a non-owner as well as for an absent id (changed 2026-09-03,
 * closing the existence oracle Requirement 17.5 forbids). That is stricter than `resolveReadAccess` — a
 * MODERATOR does not resolve a private collection here, unlike the pattern and project fallbacks — and it
 * satisfies `authenticatedFallback.ts`'s precondition that a fallback target must itself apply a gate.
 *
 * `lastKey` is the member-pattern cursor, and the round trip is symmetric with the public reader's: the
 * handler answers `encodeURIComponent(JSON.stringify(key))` and parses with
 * `JSON.parse(decodeURIComponent(lastKey))`, so the cursor is passed through verbatim.
 *
 * `opts.signal` is React Query's cancellation signal — the abort-as-cancellation seam the rest of this
 * slice keeps.
 */
export async function getCollectionAuthenticated(
  collectionId: string,
  idToken: string,
  lastKey: string | null = null,
  opts?: { signal?: AbortSignal },
): Promise<GetCollectionResponse> {
  let url = `${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}`
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`
  return secureFetch(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

export async function updateCollection(
  collectionId: string,
  data: UpdateCollectionRequest,
  idToken: string,
): Promise<UpdateCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function archiveCollection(collectionId: string, idToken: string): Promise<ArchiveCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/archive`, idToken, {
    method: 'POST',
  })
}

export async function restoreCollection(collectionId: string, idToken: string): Promise<RestoreCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/restore`, idToken, {
    method: 'POST',
  })
}

export async function createCollection(
  data: CreateCollectionRequest,
  idToken: string,
): Promise<CreateCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function addPatternToCollection(
  collectionId: string,
  patternId: string,
  idToken: string,
): Promise<AddPatternToCollectionResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/patterns/${encodeURIComponent(patternId)}`,
    idToken,
    {
      method: 'POST',
    },
  )
}

export async function removePatternFromCollection(
  collectionId: string,
  patternId: string,
  idToken: string,
): Promise<RemovePatternFromCollectionResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/patterns/${encodeURIComponent(patternId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

export async function followCollection(collectionId: string, idToken: string): Promise<FollowCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/follow`, idToken, {
    method: 'POST',
  })
}

export async function unfollowCollection(collectionId: string, idToken: string): Promise<UnfollowCollectionResponse> {
  return secureFetch(`${SECURE_API_BASE}/collections/${encodeURIComponent(collectionId)}/follow`, idToken, {
    method: 'DELETE',
  })
}
